import { useState, useEffect } from 'react';
import {
    LOCK_ASPECT_DIS_DAT,
    LOCK_ASPECT_PINTEREST,
    LOCK_ASPECT_SHARE,
    MODE_PUBLISH,
    MODE_UNPUBLISH,
    QuizSubject,
    config
} from "../Const.js";
import ImageSelector from './ImageSelector.jsx';
import QuizManager from './QuizManager.jsx';
import QuizSettings, { generateDefaultSettings } from './QuizSettings.jsx';
import {
    CircularProgress,
    Button,
    Box,
    Paper,
    Typography,
    Dialog,
    Checkbox,
    InputLabel,
    Menu,
    MenuItem,
    TextField,
    IconButton,
    DialogActions,
    DialogTitle,
    DialogContent,
    FormControl,
    Select,
    OutlinedInput
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { executeFetch, executeFetchAsync } from '../Util.js';
import ValidationDialog from '../ValidationDialog.jsx';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishIcon from '@mui/icons-material/Unpublished';
import PublishDialog from './PublishDialog.jsx';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { DisDatType, QUIZ_TYPE_DIS_DAT, QUIZ_TYPE_MULTIPLE_CHOICE } from './Const.js';
import QuizCreationDialog from './mc/QuizCreationDialog.jsx';
import TagSelector from '../TagSelector.jsx';
import useQuizAiSuggestions from '../hooks/useQuizAiSuggestions.js';

const renderFieldWithIcon = ({
    label,
    value,
    onChange,
    fieldName,
    disabled = false,
    loading = false,
    onClick
}) => {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, width: '100%', mb: '20px' }}>
            <TextField
                fullWidth
                size="small"
                label={label}
                value={value}
                variant="outlined"
                onChange={onChange}
                disabled={disabled}
            />
            <IconButton
                size="small"
                aria-label={`suggest for ${fieldName}`}
                onClick={onClick}
                disabled={loading}
            >
                {loading ? <CircularProgress size={18} /> : <AutoAwesomeIcon />}
            </IconButton>
        </Box>
    );
};


function QuizManagePanel({ questionData, onNewQuiz, onLoad }) {
    const saveUrlPath = "/api/v1/admin/quiz";
    const loadQuizPath = "/api/v1/admin/quiz";

    const [quizMetadata, setQuizMetadata] = useState(null);
    const [settings, setSettings] = useState(generateDefaultSettings());
    const [savePopupOpen, setSavePopupOpen] = useState(false);
    const [savePopupText, setSavePopupText] = useState("");
    const [quizShareImage, setQuizShareImage] = useState(null);
    const [quizPinterestImage, setQuizPinterestImage] = useState(null);
    const [quizPublished, setQuizPublished] = useState(false);
    const [managePopupOpen, setManagePopupOpen] = useState(false);
    const [settingsPopupOpen, setSettingsPopupOpen] = useState(false);
    const [savingInProgress, setSavingInProgress] = useState(false);
    const [saveValidationPopupOpen, setSaveValidationPopupOpen] = useState(false);
    const [validationErrors, setValidationErrors] = useState(null);
    const [publishPopupOpen, setPublishPopupOpen] = useState(false);
    const [newMenuAnchorEl, setNewMenuAnchorEl] = useState(null);

    const [disDat1Image, setDisDat1Image] = useState(null);
    const [disDat1ImageCaption, setDisDat1ImageCaption] = useState(null);
    const [disDat2Image, setDisDat2Image] = useState(null);
    const [disDat2ImageCaption, setDisDat2ImageCaption] = useState(null);

    const [mcCreationDialogOpen, setMcCreationDialogOpen] = useState(false);
    const isNewMenuOpen = Boolean(newMenuAnchorEl);

    const [defaultTagList, setDefaultTagList] = useState([]);
    const [entity, setEntity] = useState(null);

    const imageAltPrefixTemplate = 'Image in QuizMyDay <quiz_name> trivia quiz featuring ';
    const imageAltPrefixRegex = /(Image in QuizMyDay )(.*?)( trivia quiz featuring)/;

    const data = quizMetadata ?? {};
    const quizId = data.id;
    const type = data.type;
    const subject = data.subject ?? '';
    const difficultyLevel = data.difficultyLevel ?? '';
    const quizName = data.quizName ?? '';
    const description = data.description ?? '';
    const seoTitle = data.seoTitle ?? '';
    const mainTag = data.mainTag ?? '';
    const selectedTagList = data.tagList ?? [];
    const specificEntityType = data.specificEntityType ?? '';
    const specificEntityWikidataId = data.specificEntityWikidataId ?? '';
    const specificEntityLabel = data.specificEntityLabel ?? '';

    const safeTrim = (value) => (typeof value === "string" ? value.trim() : "");

    const replaceQuizName = (text, quizNameValue) => {
        const safeText = text ?? "";
        const safeQuizName = quizNameValue ?? "";
        return safeText.replace(imageAltPrefixRegex, `$1${safeQuizName}$3`);
    };

    const replaceAltByName = (imd, nextQuizName) => {
        if (!imd) return imd;
        const currentAlt = imd.alt ?? imd.description ?? '';
        return {
            ...imd,
            alt: replaceQuizName(currentAlt, nextQuizName)
        };
    };

    const updateQuizMetadata = (partial) => {
        setQuizMetadata(prev => {
            const merged = { ...(prev || {}), ...(partial || {}) };

            setQuizShareImage(prevShare => {
                if (!prevShare) return prevShare;
                return replaceAltByName(prevShare, merged.quizName);
            });

            setQuizPinterestImage(prevPinterest => {
                if (!prevPinterest) return prevPinterest;
                return replaceAltByName(prevPinterest, merged.quizName);
            });

            setDisDat1Image(prevImage => {
                if (!prevImage) return prevImage;
                return replaceAltByName(prevImage, merged.quizName);
            });

            setDisDat2Image(prevImage => {
                if (!prevImage) return prevImage;
                return replaceAltByName(prevImage, merged.quizName);
            });

            return merged;
        });
    };

    const setMetadataProperty = (name, value) => {
        updateQuizMetadata({ [name]: value });
    };

    const setMetadataPropertyList = (pairs) => {
        updateQuizMetadata(Object.fromEntries(pairs || []));
    };






    const applyAiSuggestionToField = (field, value) => {

        setMetadataProperty(field, value || "");
    };

    const applyPrefetchedSuggestionsToField = (field, suggestions) => {
        if (!suggestions?.length) return;

        if (field === 'quizName' && suggestions[0]?.text) {
            updateQuizMetadata({ quizName: suggestions[0].text });
            return;
        }

        if (field === 'description' && suggestions[0]?.text) {
            updateQuizMetadata({ description: suggestions[0].text });
            return;
        }

        if (field === 'seoTitle' && !quizId && suggestions[0]?.text) {
            updateQuizMetadata({ seoTitle: suggestions[0].text });
            return;
        }




    };

    const getSubjectForField = (field) => {

        return safeTrim(data.subject);
    };

    const {
        aiRunning,
        aiDialogOpen,
        aiLoading,
        aiResults,
        aiFieldName,
        aiSelectedOption,
        aiError,
        dialogCacheKey,
        loadedFromCache,
        resetAiState,
        prefetchAllAiSuggestions,
        openAiDialogForField,
        refreshAiDialogForField,
        closeAiDialog,
        applyAiSuggestion,
        setAiSelectedOption,
        formatScorePct
    } = useQuizAiSuggestions({
        quizId,
        getSubjectForField,
        onApplySuggestionToField: applyAiSuggestionToField,
        onApplyPrefetchedSuggestionsToField: applyPrefetchedSuggestionsToField
    });

    const generateQuizMetadata = (quiz) => {
        return {
            type: quiz.type,
            questionList: questionData,
            quizName: quiz.label ?? quiz.quizName ?? '',
            description: quiz.description ?? '',
            seoTitle: quiz.seoTitle ?? '',
            serialNumber: quiz.serialNumber ?? null,
            createDate: quiz.createDate ?? null,
            id: quiz.id ?? null,
            tagList: quiz.tagList ?? [],
            mainTag: quiz.mainTag ?? null,
            subject: quiz.subject ?? '',
            difficultyLevel: quiz.difficultyLevel ?? 'MEDIUM',
            specificEntityType: quiz.schemaOrgEntityList?.[0]?.entityType ?? '',
            specificEntityWikidataId: quiz.schemaOrgEntityList?.[0]?.idMapping?.WIKIDATA ?? '',
            specificEntityLabel: quiz.schemaOrgEntityList?.[0]?.label ?? '',
        };
    };

    const generateQuizSettings = (quiz) => quiz.settings;

    const handleSettingsSave = (s) => {
        setSettings(s);
        setSettingsPopupOpen(false);
    };

    const handleLoadClicked = () => setManagePopupOpen(true);
    const handlePublishClicked = () => setPublishPopupOpen(true);
    const handlePublishClosed = () => setPublishPopupOpen(false);
    const closeManagePopup = () => setManagePopupOpen(false);
    const closeSaveModal = () => setSavePopupOpen(false);
    const closeSaveValidationModal = () => setSaveValidationPopupOpen(false);
    const closeSettingsModal = () => setSettingsPopupOpen(false);

    const handlePublishOk = () => {
        const publishUrlPath = `/api/v1/admin/quiz/${quizPublished ? 'unpublish' : 'publish'}`;
        const formData = new FormData();
        formData.append('quizId', quizMetadata.id);

        executeFetch(
            config.SERVER_URL + publishUrlPath,
            "PATCH",
            null,
            formData,
            null,
            () => {
                setTimeout(() => {
                    setPublishPopupOpen(false);
                    setQuizPublished(!quizPublished);
                }, 1500);
            }
        );
    };

    const handleIncludesAudioIdentificationChange = (e) => {
        const checked = e.target.checked;
        setSettings(prev => ({ ...(prev ?? {}), includesAudioIdentification: checked }));
    };

    const handleIncludesImageIdentificationChange = (e) => {
        const checked = e.target.checked;
        setSettings(prev => ({ ...(prev ?? {}), includesImageIdentification: checked }));
    };

    const handlePopularChange = (e) => {
        const checked = e.target.checked;
        setSettings(prev => ({ ...(prev ?? {}), popular: checked }));
    };

    const handleShareImageSelected = (imd) => {
        const imageAltPrefix = replaceQuizName(imageAltPrefixTemplate, quizMetadata?.quizName);
        imd.alt = imageAltPrefix + imd.description;
        setQuizShareImage(imd);
    };

    const handlePinterestImageSelected = (imd) => {
        const imageAltPrefix = replaceQuizName(imageAltPrefixTemplate, quizMetadata?.quizName);
        imd.alt = imageAltPrefix + imd.description;
        setQuizPinterestImage(imd);
    };

    const handleShareImageAltChange = (value) => {
        setQuizShareImage(prev => ({ ...prev, alt: value }));
    };

    const handlePinterestImageAltChange = (value) => {
        setQuizPinterestImage(prev => ({ ...prev, alt: value }));
    };

    const generateDisDatShareImage = async (imd1, imd2) => {
        const url = `${config.SERVER_URL}/api/v1/admin/image/generateDisDatImageMetadata`;
        const form = new FormData();
        form.append('imd1', JSON.stringify(imd1));
        form.append('imd2', JSON.stringify(imd2));
        form.append('caption1', disDat1ImageCaption);
        form.append('caption2', disDat2ImageCaption);

        const resp = await executeFetchAsync(url, 'POST', null, form);
        setQuizShareImage(resp);
        return resp;
    };

    const generateDisDatPinterestImage = async (imd1, imd2) => {
        const url = `${config.SERVER_URL}/api/v1/admin/image/generateDisDatPinterestImageMetadata`;
        const form = new FormData();
        form.append('imd1', JSON.stringify(imd1));
        form.append('imd2', JSON.stringify(imd2));
        form.append('caption1', disDat1ImageCaption);
        form.append('caption2', disDat2ImageCaption);

        const resp = await executeFetchAsync(url, 'POST', null, form);
        setQuizPinterestImage(resp);
        return resp;
    };

    const handleDisDat1ImageSelected = (imd) => {
        setDisDat1Image(imd);
        if (disDat2Image) {
            void generateDisDatShareImage(imd, disDat2Image);
            void generateDisDatPinterestImage(imd, disDat2Image);
        }
    };

    const handleDisDat2ImageSelected = (imd) => {
        setDisDat2Image(imd);
        if (disDat1Image) {
            void generateDisDatShareImage(disDat1Image, imd);
            void generateDisDatPinterestImage(disDat1Image, imd);
        }
    };

    const handleDisDatImage1CaptionChanged = (e) => setDisDat1ImageCaption(e.currentTarget.value);
    const handleDisDatImage2CaptionChanged = (e) => setDisDat2ImageCaption(e.currentTarget.value);

    const handleNewClicked = (e) => setNewMenuAnchorEl(e.currentTarget);
    const handleNewMenuClose = () => setNewMenuAnchorEl(null);

    const initNewQuiz = (newType, selectedEntity, quizSubject, aiQuestionsSubjectText) => {
        setSettings(generateDefaultSettings());
        setQuizMetadata(generateQuizMetadata({ type: newType }));
        setQuizShareImage(null);
        setQuizPinterestImage(null);
        setDisDat1Image(null);
        setDisDat2Image(null);
        setDisDat1ImageCaption(null);
        setDisDat2ImageCaption(null);
        setQuizPublished(false);
        setDefaultTagList([]);
        setEntity(selectedEntity ?? null);
        resetAiState();
        onNewQuiz(newType, selectedEntity, quizSubject, aiQuestionsSubjectText);
    };

    const handleNewQuizTypeSelected = (newType) => {
        handleNewMenuClose();

        if (newType === QUIZ_TYPE_DIS_DAT) {
            initNewQuiz(newType);
        }

        if (newType === QUIZ_TYPE_MULTIPLE_CHOICE) {
            setMcCreationDialogOpen(true);
        }
    };

    const getQuizTitleAITextBySubject = (quizSubject, entityLabel, subItemLabel) => {
        switch (quizSubject) {
            case QuizSubject.GENERAL:
                return `${entityLabel}`;
            case QuizSubject.ALBUMS:
                return `${entityLabel} albums`;
            case QuizSubject.SONGS:
                return `${entityLabel} songs`;
            case QuizSubject.AWARDS:
                return `${entityLabel} awards & achievements`;
            case QuizSubject.COLLABORATIONS:
                return `${entityLabel} collaborations`;
            case QuizSubject.COMPLETE_TITLES:
                return `${entityLabel} song titles - Find the missing word`;
            case QuizSubject.DEEP_CUTS:
                return `${entityLabel} deep cuts`;
            case QuizSubject.EARLY_YEARS:
                return `${entityLabel} early years`;
            case QuizSubject.MATCH_SONGS_TO_ALBUMS:
                return `${entityLabel} - Match Songs to Albums`;
            case QuizSubject.MEMBERS:
                return `${entityLabel} band members`;
            case QuizSubject.PERSONAL_LIFE:
                return `${entityLabel} personal life`;
            case QuizSubject.QUOTES:
                return `${entityLabel} quotes`;
            case QuizSubject.TOURS:
                return `${entityLabel} tours & live concerts`;
            case QuizSubject.SPECIFIC_ALBUM:
                return `${entityLabel} album '${subItemLabel}'`;
            case QuizSubject.SPECIFIC_SONG:
                return `${entityLabel} song '${subItemLabel}'`;
            default:
                return `${entityLabel}`;
        }
    };

    const getQuizDescriptionAITextBySubject = (quizSubject, entityLabel, subItemLabel) =>
        getQuizTitleAITextBySubject(quizSubject, entityLabel, subItemLabel);

    const getSeoSlugAITextBySubject = (quizSubject, entityLabel, subItemLabel) =>
        getQuizTitleAITextBySubject(quizSubject, entityLabel, subItemLabel);


    const fetchAlbumByMusicBrainzId = async (musicBrainzId) => {
        const trimmedMbid = safeTrim(musicBrainzId);
        if (!trimmedMbid) return null;

        const url = `${config.SERVER_URL}/api/v1/admin/entityData/musicbrainz/${encodeURIComponent(trimmedMbid)}`;
        const result = await executeFetchAsync(url, "GET", null, null, null);

        updateQuizMetadata({
            specificEntityType: 'MUSIC_ALBUM',
            specificEntityWikidataId: result?.idMapping?.WIKIDATA ?? '',
            specificEntityLabel: result?.label ?? ''
        });

        return result;
    };

    const handleMcDialogSelect = async (selectionData) => {
        const selectedEntity = selectionData?.entity;
        const entityType = selectionData?.entityType;
        const entityLabel = selectionData?.entity?.label ?? '';
        const quizSubject = selectionData?.subject ?? '';
        const subItem = selectionData?.subItem ?? null;
        const subItemLabel = subItem?.label ?? '';
        const subItemMusicBrainzId = subItem?.idMapping?.MUSICBRAINZ ?? '';

        const isOtherSubject = entityType.trim().toLowerCase() === 'other';

        if (isOtherSubject) {
            initNewQuiz(QUIZ_TYPE_MULTIPLE_CHOICE, null);
            setMcCreationDialogOpen(false);
        } else {
            const titleSubject = getQuizTitleAITextBySubject(quizSubject, entityLabel, subItemLabel);
            const descriptionSubject = getQuizDescriptionAITextBySubject(quizSubject, entityLabel, subItemLabel);
            const slugSubject = getSeoSlugAITextBySubject(quizSubject, entityLabel, subItemLabel);
            const aiQuestionsSubjectText = titleSubject;

            initNewQuiz(QUIZ_TYPE_MULTIPLE_CHOICE, selectedEntity, quizSubject, aiQuestionsSubjectText);

            updateQuizMetadata({
                subject: titleSubject
            });

            setMcCreationDialogOpen(false);

            if (quizSubject === QuizSubject.SPECIFIC_ALBUM && subItemMusicBrainzId) {
                try {
                    await fetchAlbumByMusicBrainzId(subItemMusicBrainzId);
                } catch (err) {
                    console.error("Failed fetching album by MusicBrainz ID", err);
                    updateQuizMetadata({
                        specificEntityType: 'MUSIC_ALBUM',
                        specificEntityWikidataId: '',
                        specificEntityLabel: subItemLabel ?? ''
                    });
                }
            }

            await prefetchAllAiSuggestions({
                titleSubject,
                descriptionSubject,
                slugSubject,
                forceRefresh: true,
                applyDefaults: true
            });
        }
    };

    const handleLoadQuiz = (quiz) => {
        const url = `${config.SERVER_URL}${loadQuizPath}/${quiz.id}`;

        executeFetch(url, "get", null, null, null, (result) => {
            setQuizMetadata(generateQuizMetadata(result));
            setSettings(generateQuizSettings(result));
            onLoad && onLoad(result);
            setQuizShareImage(result.shareImageMetadata);
            setQuizPinterestImage(result.pinterestImageMetadata);
            setQuizPublished(result.published);
            setManagePopupOpen(false);
            resetAiState();

            if (result.type === QUIZ_TYPE_DIS_DAT) {
                setEntity(null);
                const questionParams = result.questionList[0].criteria.questionParams;
                setDisDat1Image(questionParams.option1QVariable.imageQVariable.imageMetadata);
                setDisDat2Image(questionParams.option2QVariable.imageQVariable.imageMetadata);
                setDisDat1ImageCaption(questionParams.option1QVariable.imageQVariable.caption ?? null);
                setDisDat2ImageCaption(questionParams.option2QVariable.imageQVariable.caption ?? null);
            } else if (result.type === QUIZ_TYPE_MULTIPLE_CHOICE) {
                const loadedEntity =
                    result.questionList?.[0]?.criteria?.questionParams?.quizzlerEntity ?? null;
                setEntity(loadedEntity);
            } else {
                setEntity(null);
            }
        });
    };

    const generateQuiz = () => {
        const quiz = {
            type: data.type,
            label: data.quizName,
            description: data.description,
            site: data.site,
            seoTitle: data.seoTitle,
            createDate: data.createDate,
            id: data.id,
            serialNumber: data.serialNumber,
            tagList: data.tagList,
            mainTag: data.mainTag,
            subject: data.subject,
            published: quizPublished,
            settings,
            difficultyLevel: data.difficultyLevel,
            questionList: [...questionData]
        };

        if (quizShareImage && !quizShareImage.alt) {
            quizShareImage.alt = quizShareImage?.description;
        }

        quiz.shareImageMetadata = quizShareImage;

        if (quizPinterestImage) {
            quiz.pinterestImageMetadata = quizPinterestImage;
        }

        if (data.specificEntityType !== '') {
            quiz.schemaOrgEntityList = [{
                entityType: data.specificEntityType,
                idMapping: { WIKIDATA: data.specificEntityWikidataId },
                label: data.specificEntityLabel
            }];
        }

        if (data.type === QUIZ_TYPE_DIS_DAT) {
            const questionParams = quiz.questionList[0].criteria.questionParams;

            questionParams.option1QVariable.imageQVariable = {
                imageMetadata: disDat1Image,
                caption: disDat1ImageCaption
            };
            questionParams.option2QVariable.imageQVariable = {
                imageMetadata: disDat2Image,
                caption: disDat2ImageCaption
            };
        } else {
            quiz.questionList.forEach(q => {
                delete q.criteria.contextQVariableList;
                delete q.criteria.correctOptionQVariableList;
                delete q.criteria.distractorQVariableList;
                delete q.criteria.quizzlerEntityList;
            });
        }

        return quiz;
    };

    const validateQuiz = () => {
        const missing = [];

        if (!data?.quizName) missing.push('Label');
        if (!data?.mainTag) missing.push('Main Tag');
        if (!quizShareImage) missing.push('Quiz Share Image');

        if (data.type === QUIZ_TYPE_DIS_DAT) {
            if (!disDat1Image) missing.push('disDat1Image');
            if (!disDat2Image) missing.push('disDat2Image');
        }

        return missing;
    };

    const handleSaveClicked = async () => {
        const validationResult = validateQuiz();

        if (validationResult.length > 0) {
            setValidationErrors(validationResult);
            setSaveValidationPopupOpen(true);
            return;
        }

        setSavePopupOpen(true);
        setSavingInProgress(true);
        setSavePopupText("Saving...");

        try {
            const result = await executeFetchAsync(
                config.SERVER_URL + saveUrlPath,
                "POST",
                { "Content-Type": "application/json" },
                JSON.stringify(generateQuiz()),
                null
            );

            setSavingInProgress(false);
            handleLoadQuiz(result);
            setSavePopupText("Saved Successfully");
            setTimeout(() => setSavePopupOpen(false), 1500);
        } catch (error) {
            setSavingInProgress(false);
            setSavePopupText("Save failed " + error);
        }
    };

    const handleAIClicked = async () => {
        if (!safeTrim(data.subject)) {
            alert("Please enter a subject.");
            return;
        }

        await prefetchAllAiSuggestions({
            titleSubject: data.subject.trim(),
            descriptionSubject: data.subject.trim(),
            slugSubject: data.subject.trim(),
            forceRefresh: true,
            applyDefaults: true
        });
    };

    const getSelectedEntityIds = () => {

        if (type === QUIZ_TYPE_MULTIPLE_CHOICE) {
            const entityWikidataId = entity?.id;
            return entityWikidataId ? [entityWikidataId] : [];
        }

        let selectedEntityIds = [];

        if (type === QUIZ_TYPE_DIS_DAT) {
            const questionParams = questionData?.[0]?.criteria?.questionParams;
            const disDatType = questionParams?.disDatType;
            let commonQuizzlerEntity;

            switch (disDatType) {
                case DisDatType.CROSS_ENTITY_MUSICAL_GROUP_VS_MUSICAL_GROUP:
                case DisDatType.CROSS_ENTITY_MUSICIAN_VS_MUSICIAN: {
                    const option1QVariable = questionParams.option1QVariable;
                    const option2QVariable = questionParams.option2QVariable;
                    if (option1QVariable) selectedEntityIds.push(option1QVariable.quizzlerObject.idMapping.WIKIDATA);
                    if (option2QVariable) selectedEntityIds.push(option2QVariable.quizzlerObject.idMapping.WIKIDATA);
                    break;
                }

                case DisDatType.MUSICAL_GROUP_MEMBER_VS_MEMBER:
                case DisDatType.MUSICAL_GROUP_ALBUM_VS_ALBUM:
                case DisDatType.MUSICIAN_ALBUM_VS_ALBUM:
                    commonQuizzlerEntity = questionParams.quizzlerEntity;
                    if (commonQuizzlerEntity) {
                        selectedEntityIds.push(commonQuizzlerEntity.idMapping.WIKIDATA);
                    }
                    break;

                default:
                    break;
            }
        }

        return selectedEntityIds.filter(Boolean);
    };

    const generateEntityTagUrl = () => {
        const selectedEntityIds = getSelectedEntityIds();
        if (selectedEntityIds.length === 0) return null;
        return `${config.SERVER_URL}/api/v1/admin/entityData/tags?wikidataIdList=${selectedEntityIds.join(",")}`;
    };

    useEffect(() => {
        const fetchDefaultTagListAsync = async () => {
            const url = generateEntityTagUrl();

            if (!url) {
                setDefaultTagList([]);
                return;
            }

            try {
                const result = await executeFetchAsync(url, "get", null, null, null);
                setDefaultTagList(result || []);
            } catch (err) {
                console.error("Failed fetching default tag list", err);
                setDefaultTagList([]);
            }
        };

        if (entity?.id || (questionData && questionData.length > 0)) {
            fetchDefaultTagListAsync();
        } else {
            setDefaultTagList([]);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questionData, type, entity]);

    useEffect(() => {
        if (defaultTagList?.length > 0 && type === QUIZ_TYPE_MULTIPLE_CHOICE) {
            handleTagGenerateClicked();
        }
        // eslint-disable-next-line
    }, [defaultTagList]);

    const calcMainEntity = () => {
        let isMultipleMaxElements = false;
        const frequencyMap = {};

        (questionData || []).forEach(q => {
            const qe = q?.criteria?.questionParams?.quizzlerEntity;
            if (qe?.idMapping?.WIKIDATA) {
                frequencyMap[qe.idMapping.WIKIDATA] = (frequencyMap[qe.idMapping.WIKIDATA] || 0) + 1;
            }
        });

        let maxFrequency = 0;
        let mostFrequentElement;

        for (const key in frequencyMap) {
            if (frequencyMap[key] > maxFrequency) {
                maxFrequency = frequencyMap[key];
                mostFrequentElement = key;
                isMultipleMaxElements = false;
            } else if (frequencyMap[key] === maxFrequency) {
                isMultipleMaxElements = true;
            }
        }

        return isMultipleMaxElements ? null : mostFrequentElement;
    };

    const calcMainTag = () => {
        if (type === QUIZ_TYPE_MULTIPLE_CHOICE && entity) {
            const entityWikidataId = entity?.id;
            return defaultTagList.find(t => t.idMapping?.WIKIDATA === entityWikidataId) ?? null;
        }

        const mainEntityTag = calcMainEntity();
        return mainEntityTag
            ? defaultTagList.find(t => t.idMapping?.WIKIDATA === mainEntityTag) ?? null
            : null;
    };

    const handleTagValuesChanged = (tagList) => setMetadataProperty('tagList', tagList);
    const handleMainTagChanged = (newMainTag) => setMetadataProperty('mainTag', newMainTag);

    const handleTagGenerateClicked = () => {
        if (type === QUIZ_TYPE_MULTIPLE_CHOICE) {
            const generatedMainTag = calcMainTag();

            if (generatedMainTag) {
                setMetadataPropertyList([
                    ['mainTag', generatedMainTag],
                    ['tagList', defaultTagList]
                ]);
            } else {
                setMetadataProperty('tagList', defaultTagList);
            }

            return;
        }

        if (type === QUIZ_TYPE_DIS_DAT) {
            const questionParams = questionData?.[0]?.criteria?.questionParams;
            if (!questionParams) return;

            const disDatType = questionParams.disDatType;
            const qv1 = questionParams.option1QVariable;
            const qv2 = questionParams.option2QVariable;
            let e1, e2, e3, mainQuizzlerEntity;

            switch (disDatType) {
                case DisDatType.CROSS_ENTITY_MUSICIAN_VS_MUSICIAN:
                case DisDatType.CROSS_ENTITY_MUSICAL_GROUP_VS_MUSICAL_GROUP:
                    e1 = { label: qv1.label, idMapping: qv1.quizzlerObject.idMapping };
                    e2 = { label: qv2.label, idMapping: qv2.quizzlerObject.idMapping };
                    setMetadataPropertyList([
                        ['mainTag', e1],
                        ['tagList', [...defaultTagList, e1, e2]]
                    ]);
                    break;

                case DisDatType.MUSICAL_GROUP_MEMBER_VS_MEMBER:
                    mainQuizzlerEntity = questionParams.quizzlerEntity;
                    e1 = { label: qv1.label, idMapping: qv1.quizzlerObject.idMapping };
                    e2 = { label: qv2.label, idMapping: qv2.quizzlerObject.idMapping };
                    e3 = { label: mainQuizzlerEntity.label, idMapping: mainQuizzlerEntity.idMapping };
                    setMetadataPropertyList([
                        ['mainTag', e3],
                        ['tagList', [...defaultTagList, e1, e2]]
                    ]);
                    break;

                case DisDatType.MUSICAL_GROUP_ALBUM_VS_ALBUM:
                case DisDatType.MUSICIAN_ALBUM_VS_ALBUM:
                    mainQuizzlerEntity = questionParams.quizzlerEntity;
                    e1 = { label: mainQuizzlerEntity.label, idMapping: mainQuizzlerEntity.idMapping };
                    setMetadataPropertyList([
                        ['mainTag', e1],
                        ['tagList', [...defaultTagList]]
                    ]);
                    break;

                default:
                    break;
            }
        }
    };

    const difficultyFriendlyLabelFromValue = (val) => {
        if (!val) return <em style={{ color: 'rgba(0,0,0,0.6)' }}>Select difficulty</em>;
        const map = { HARD: 'Hard', MEDIUM: 'Medium', EASY: 'Easy' };
        return map[val] ?? val;
    };


    return (
        <Box sx={{ height: '100%' }}>
            <Typography variant="h6">Manage</Typography>
            <Box sx={{ height: 3 }} />

            <Paper elevation={10} sx={{ height: '92%', overflowY: 'auto', overflowX: 'hidden', padding: '20px' }}>
                <Box sx={{ width: '100%', display: 'flex', gap: '5px', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                        <Button variant="outlined" size="small" startIcon={<AddCircleIcon />} onClick={handleNewClicked}>
                            New
                        </Button>

                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ManageSearchIcon fontSize="small" />}
                            onClick={handleLoadClicked}
                        >
                            Load
                        </Button>

                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={aiRunning ? <CircularProgress size={18} /> : <AutoAwesomeIcon />}
                            onClick={handleAIClicked}
                            disabled={aiRunning}
                        >
                            {aiRunning ? "Generating…" : "Generate"}
                        </Button>
                    </Box>

                    <Box sx={{ display: 'flex', gap: '5px' }}>
                        {!quizPublished && (
                            <Button
                                variant="contained"
                                sx={{
                                    backgroundColor: 'green',
                                    color: 'white',
                                    '&:hover': { backgroundColor: 'green' },
                                }}
                                disabled={!quizMetadata?.id}
                                size="small"
                                startIcon={<PublishIcon fontSize="small" />}
                                onClick={handlePublishClicked}
                            >
                                Publish
                            </Button>
                        )}

                        {quizPublished && (
                            <Button
                                variant="contained"
                                color="error"
                                size="small"
                                startIcon={<UnpublishIcon fontSize="small" />}
                                onClick={handlePublishClicked}
                            >
                                Unpublish
                            </Button>
                        )}

                        <Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={handleSaveClicked}>
                            Save
                        </Button>
                    </Box>
                </Box>

                <Dialog
                    open={settingsPopupOpen}
                    PaperProps={{ sx: { width: 500, height: 500 } }}
                    onClose={closeSettingsModal}
                >
                    <QuizSettings settings={settings} onSave={handleSettingsSave} onCancel={closeSettingsModal} />
                </Dialog>

                <Box sx={{ height: 20 }} />

                <TextField
                    fullWidth
                    size="small"
                    label="Subject"
                    value={subject}
                    variant="outlined"
                    onChange={(e) => setMetadataProperty('subject', e.target.value)}
                    sx={{ mb: '20px', mt: 2 }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="difficulty-level-label" shrink>
                            Difficulty Level
                        </InputLabel>

                        <Select
                            labelId="difficulty-level-label"
                            id="difficulty-level-select"
                            value={difficultyLevel}
                            label="Difficulty Level"
                            onChange={(e) => setMetadataProperty('difficultyLevel', e.target.value)}
                            displayEmpty
                            renderValue={(selected) => difficultyFriendlyLabelFromValue(selected)}
                        >
                            <MenuItem value="">
                                <em>Select difficulty</em>
                            </MenuItem>
                            <MenuItem value="HARD">Hard</MenuItem>
                            <MenuItem value="MEDIUM">Medium</MenuItem>
                            <MenuItem value="EASY">Easy</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {renderFieldWithIcon({
                    label: "Quiz Title",
                    value: quizName,
                    onChange: (e) => setMetadataProperty('quizName', e.target.value),
                    fieldName: "quizName",
                    loading: !!aiLoading.quizName,
                    onClick: () => openAiDialogForField('quizName')
                })}

                {renderFieldWithIcon({
                    label: "Quiz Description",
                    value: description,
                    onChange: (e) => setMetadataProperty('description', e.target.value),
                    fieldName: "description",
                    loading: !!aiLoading.description,
                    onClick: () => openAiDialogForField('description')
                })}

                {renderFieldWithIcon({
                    label: "SEO Slug",
                    value: seoTitle,
                    onChange: (e) => setMetadataProperty('seoTitle', e.target.value),
                    fieldName: "seoTitle",
                    disabled: !!quizId,
                    loading: !!aiLoading.seoTitle,
                    onClick: () => openAiDialogForField('seoTitle')
                })}





                <Box sx={{ mb: '20px' }}>
                    <TagSelector
                        mainTag={mainTag}
                        selected={selectedTagList}
                        onMainTagChange={handleMainTagChanged}
                        onTagValuesChange={handleTagValuesChanged}
                    />
                    <Button size="small" sx={{ mt: '3px' }} onClick={handleTagGenerateClicked}>
                        Generate
                    </Button>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: '20px', alignItems: 'flex-start' }}>
                    <FormControl sx={{ width: '250px' }} size="small" variant="outlined">
                        <InputLabel id="specific-entity-type-label" shrink>
                            Schema.org Type
                        </InputLabel>

                        <Select
                            labelId="specific-entity-type-label"
                            id="specific-entity-type"
                            value={specificEntityType}
                            onChange={(e) => setMetadataProperty('specificEntityType', e.target.value ?? "")}
                            input={<OutlinedInput label="Specific Entity Type" notched />}
                            displayEmpty
                        >
                            <MenuItem value=""></MenuItem>
                            <MenuItem value="MUSIC_ALBUM">Album</MenuItem>
                            <MenuItem value="MUSIC_SONG">Song</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        sx={{ width: '250px' }}
                        size="small"
                        label="wikidataID"
                        value={specificEntityWikidataId}
                        variant="outlined"
                        onChange={(e) => setMetadataProperty('specificEntityWikidataId', e.target.value ?? "")}
                        placeholder="e.g., Q190640"
                    />

                    <TextField
                        fullWidth
                        size="small"
                        label="Label"
                        value={specificEntityLabel}
                        variant="outlined"
                        onChange={(e) => setMetadataProperty('specificEntityLabel', e.target.value ?? "")}
                        placeholder="e.g., Born To Run"
                    />
                </Box>

                <Box sx={{ mb: '20px' }}>
                    <InputLabel>
                        <Checkbox sx={{ padding: '5px' }} checked={settings.popular} onChange={handlePopularChange} />
                        Popular Quiz
                    </InputLabel>

                    <InputLabel>
                        <Checkbox
                            sx={{ padding: '5px' }}
                            checked={settings.includesImageIdentification}
                            onChange={handleIncludesImageIdentificationChange}
                        />
                        Includes Image Identification
                    </InputLabel>

                    <InputLabel>
                        <Checkbox
                            sx={{ padding: '5px' }}
                            checked={settings.includesAudioIdentification}
                            onChange={handleIncludesAudioIdentificationChange}
                        />
                        Includes Audio Identification
                    </InputLabel>
                </Box>

                <Dialog
                    open={managePopupOpen}
                    PaperProps={{ sx: { width: '90%', maxWidth: '90%' } }}
                    onClose={closeManagePopup}
                >
                    <QuizManager onLoadQuiz={handleLoadQuiz} />
                </Dialog>

                {quizMetadata?.type === QUIZ_TYPE_DIS_DAT && (
                    <>
                        <Box sx={{ width: '100%' }}>
                            DisDat Image 1
                            <ImageSelector
                                imd={disDat1Image}
                                lockAspect={LOCK_ASPECT_DIS_DAT}
                                onSelect={handleDisDat1ImageSelected}
                                searchStr={questionData[0]?.criteria?.questionParams?.option1QVariable?.label}
                            />
                        </Box>

                        <Box sx={{ width: '100%' }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Caption"
                                value={disDat1ImageCaption ?? ''}
                                variant="outlined"
                                onChange={handleDisDatImage1CaptionChanged}
                                sx={{ mb: '20px', mt: 2 }}
                            />
                        </Box>

                        <Box sx={{ width: '100%', mt: '20px' }}>
                            DisDat Image 2
                            <ImageSelector
                                imd={disDat2Image}
                                lockAspect={LOCK_ASPECT_DIS_DAT}
                                onSelect={handleDisDat2ImageSelected}
                                searchStr={questionData[0]?.criteria?.questionParams?.option2QVariable?.label}
                            />
                        </Box>

                        <Box sx={{ width: '100%' }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Caption"
                                value={disDat2ImageCaption ?? ''}
                                variant="outlined"
                                onChange={handleDisDatImage2CaptionChanged}
                                sx={{ mb: '20px', mt: 2 }}
                            />
                        </Box>
                    </>
                )}

                <Box sx={{ width: '100%', mt: '20px' }}>
                    Quiz Share Image
                    <ImageSelector
                        imd={quizShareImage}
                        onAltChange={handleShareImageAltChange}
                        lockAspect={LOCK_ASPECT_SHARE}
                        onSelect={handleShareImageSelected}
                        searchStr={data?.mainTag?.idMapping?.WIKIDATA ? `"${data?.mainTag?.label}"` : null}
                    />
                </Box>

                <Box sx={{ width: '100%', mt: '20px' }}>
                    Pinterest Pin Image
                    <ImageSelector
                        imd={quizPinterestImage}
                        onAltChange={handlePinterestImageAltChange}
                        lockAspect={LOCK_ASPECT_PINTEREST}
                        onSelect={handlePinterestImageSelected}
                        searchStr={data?.mainTag?.idMapping?.WIKIDATA ? `"${data?.mainTag?.label}"` : null}
                    />
                </Box>

                <ValidationDialog
                    title="Missing Fields"
                    open={saveValidationPopupOpen}
                    errors={validationErrors}
                    onClose={closeSaveValidationModal}
                />

                <PublishDialog
                    mode={quizPublished ? MODE_UNPUBLISH : MODE_PUBLISH}
                    quizLabel={data?.quizName}
                    open={publishPopupOpen}
                    onClose={handlePublishClosed}
                    onOk={handlePublishOk}
                />

                <Dialog
                    open={savePopupOpen}
                    PaperProps={{ sx: { width: 400, height: 100 } }}
                    onClose={closeSaveModal}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            height: '100%',
                            width: '100%',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        {savingInProgress ? (
                            <Box sx={{ padding: '20px' }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex' }}>
                                <Typography variant="h6">{savePopupText}</Typography>
                            </Box>
                        )}
                    </Box>
                </Dialog>
            </Paper>

            <Menu anchorEl={newMenuAnchorEl} open={isNewMenuOpen} onClose={handleNewMenuClose}>
                <MenuItem onClick={() => handleNewQuizTypeSelected(QUIZ_TYPE_MULTIPLE_CHOICE)}>
                    Multiple Choice
                </MenuItem>
                <MenuItem onClick={() => handleNewQuizTypeSelected(QUIZ_TYPE_DIS_DAT)}>
                    DisDat
                </MenuItem>
            </Menu>

            <QuizCreationDialog
                open={mcCreationDialogOpen}
                onClose={() => setMcCreationDialogOpen(false)}
                onSelect={handleMcDialogSelect}
            />

            <Dialog open={aiDialogOpen} onClose={closeAiDialog} fullWidth maxWidth="md">
                <DialogTitle>AI suggestions</DialogTitle>

                <DialogContent>
                    {(aiLoading[dialogCacheKey] ?? false) && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {!(aiLoading[dialogCacheKey] ?? false) && aiError && (
                        <Typography color="error">{aiError}</Typography>
                    )}

                    {!(aiLoading[dialogCacheKey] ?? false) && !aiError && aiResults.length === 0 && (
                        <Typography>No suggestions returned.</Typography>
                    )}

                    {!(aiLoading[dialogCacheKey] ?? false) && aiResults.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="ai-suggestion-select-label">Choose suggestion</InputLabel>

                                <Select
                                    labelId="ai-suggestion-select-label"
                                    value={aiSelectedOption}
                                    label="Choose suggestion"
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setAiSelectedOption(value);
                                        applyAiSuggestion(aiFieldName, value);
                                    }}
                                    MenuProps={{
                                        PaperProps: { sx: { maxHeight: 300, maxWidth: '100ch' } },
                                        MenuListProps: { sx: { whiteSpace: 'pre-wrap' } }
                                    }}
                                    renderValue={(selected) => (
                                        <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: '100ch' }}>
                                            {selected}
                                        </Box>
                                    )}
                                >
                                    {aiResults.map((r, idx) => {
                                        const text = r?.text ?? "";
                                        const score01 = r?.score ?? null;
                                        const correctnessResponse = r?.correctnessResponse ?? null;

                                        return (
                                            <MenuItem key={idx} value={text} sx={{ alignItems: 'flex-start', py: 1 }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                                    <Typography
                                                        sx={{
                                                            whiteSpace: 'pre-wrap',
                                                            wordBreak: 'break-word',
                                                            display: 'block',
                                                            maxWidth: '100ch'
                                                        }}
                                                    >
                                                        {text}
                                                    </Typography>

                                                    <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.5 }}>
                                                        {score01 != null ? `Confidence: ${formatScorePct(score01)}` : ''}
                                                    </Typography>

                                                    <Typography
                                                        variant="caption"
                                                        component="div"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            mt: 0.5,
                                                            whiteSpace: 'normal',
                                                            wordBreak: 'break-word',
                                                            display: 'block',
                                                            maxWidth: '100ch',
                                                            lineHeight: 1.2,
                                                        }}
                                                    >
                                                        {correctnessResponse}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>

                            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                                Showing {aiResults.length} suggestion(s). {loadedFromCache ? 'Loaded from cache.' : ''}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={closeAiDialog}>Cancel</Button>
                    <Button
                        onClick={refreshAiDialogForField}
                        disabled={!aiFieldName || (aiLoading[dialogCacheKey] ?? false)}
                    >
                        Refresh
                    </Button>
                    <Button
                        onClick={() => applyAiSuggestion(aiFieldName, aiSelectedOption || aiResults[0]?.text || "")}
                        disabled={(aiLoading[dialogCacheKey] ?? false) || aiResults.length === 0}
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default QuizManagePanel;